import React from 'react';
import { Activity, TrendingUp, Droplets, Heart, Shield, Info, AlertCircle } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const BloodMarkersPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Activity className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary mb-1">Information & Education</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Understanding Blood Markers in CLL
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Understanding Blood Markers in CLL"
            category="CLL"
            sourcePage="Understanding Blood Markers in CLL"
            sourceUrl="/blood-markers"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: Why blood tests matter */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Why blood tests matter in CLL
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Blood tests are one of the main ways Chronic Lymphocytic Leukaemia (CLL) is monitored. They help doctors understand how the condition is behaving over time.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 my-4">
              <p className="text-slate-600 mb-1">Single numbers can be misleading.</p>
              <p className="font-semibold text-slate-900">Trends over time tell the real story.</p>
            </div>
            <p>
              This is why regular testing and consistent monitoring are important in CLL.
            </p>
          </div>
        </div>

        {/* Section 2: Why trends matter */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-blue-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Why trends matter more than "normal ranges"
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Reference ranges are general guidelines based on the wider population. Many people with CLL live well outside these ranges and feel completely well.
                </p>
                <p className="mb-3">Doctors are usually more interested in:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Direction of change</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Speed of change</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Consistency over time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Symptoms alongside results</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  A result slightly outside the reference range is often less important than how it is changing over months or years.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Common blood markers */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-6">
            Common blood markers you may see
          </h2>
          
          {/* WBC */}
          <div className="mb-6 pb-6 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Droplets className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">White Blood Cells (WBC)</h3>
            </div>
            <div className="ml-13 space-y-2 text-slate-700 leading-relaxed pl-13">
              <p>
                This measures the total number of white blood cells in your blood. In CLL, this number is often elevated due to an increased number of lymphocytes.
              </p>
              <p className="text-slate-600 italic">
                A gradual rise is common in CLL and does not automatically mean treatment is needed.
              </p>
            </div>
          </div>

          {/* Lymphocytes */}
          <div className="mb-6 pb-6 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Lymphocytes</h3>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                Lymphocytes are the key cells involved in CLL. They are a type of white blood cell that normally helps fight infection.
              </p>
              <p className="text-slate-600 italic">
                In CLL, lymphocyte counts may increase slowly over time. Doctors often watch how quickly this number changes rather than focusing on a single value.
              </p>
            </div>
          </div>

          {/* Platelets */}
          <div className="mb-6 pb-6 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Platelets</h3>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                Platelets help with blood clotting. Lower platelet levels can sometimes occur later in the course of CLL.
              </p>
              <p className="text-slate-600 italic">
                Changes in platelets are usually assessed alongside other blood markers and symptoms.
              </p>
            </div>
          </div>

          {/* Haemoglobin */}
          <div className="mb-6 pb-6 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                <Heart className="h-5 w-5 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Haemoglobin</h3>
            </div>
            <div className="text-slate-700 leading-relaxed">
              <p>
                Haemoglobin reflects red blood cell levels and oxygen delivery around the body. Lower levels may contribute to fatigue or shortness of breath.
              </p>
            </div>
          </div>

          {/* Neutrophils */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Neutrophils</h3>
            </div>
            <div className="text-slate-700 leading-relaxed">
              <p>
                Neutrophils help fight bacterial infections. Lower neutrophil levels may increase infection risk and are monitored carefully.
              </p>
            </div>
          </div>
        </div>

        {/* Section 4: Putting results into context */}
        <div className="p-6 md:p-8 bg-green-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Putting results into context
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Blood results are always interpreted together with:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Symptoms</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Physical examination findings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Medical history</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Overall trends over time</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  No single blood test result should be viewed in isolation.
                </p>
                <p className="mt-4 text-slate-700">
                  This platform is designed to help you visualise trends, organise information, and prepare informed questions for your healthcare appointments — not to replace medical advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Blood test results must always be interpreted by a qualified healthcare professional in the context of your individual health.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Understanding Blood Markers in CLL"
          category="CLL"
          sourcePage="Understanding Blood Markers in CLL"
          sourceUrl="/blood-markers"
        />
      </div>
    </div>
  );
};

export default BloodMarkersPage;
